import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/webpack.mjs';
import TextBlock from '@component/common/block/text-block';
import { acquirePdfDocument } from '@util/pdf-document-cache';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  file: File;
  page: number;
  handleMetadata: (metadata: { ratio: number; totalPages: number }) => void;
}

const StatusContainer = styled.div`
  position: absolute;
  padding: ${size.MARGIN_2};
`;

const PdfBackground: React.FC<IProps> = ({ file, page, handleMetadata }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loaded, setLoaded] = useState<{ file: File; document: PDFDocumentProxy } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    const url = URL.createObjectURL(file);
    const handle = acquirePdfDocument(url);
    setLoaded(null);
    setError('');
    setLoading(true);
    void handle.document
      .then((document) => {
        if (!disposed) setLoaded({ file, document });
      })
      .catch(() => {
        if (!disposed) {
          setError('PDF를 열지 못했어요. 다른 파일을 선택해주세요.');
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
      handle.release();
      URL.revokeObjectURL(url);
    };
  }, [file]);

  useEffect(() => {
    if (!loaded || loaded.file !== file) return;
    let disposed = false;
    let task: RenderTask | undefined;
    setError('');
    setLoading(true);

    const render = async () => {
      try {
        const pdfPage = await loaded.document.getPage(page);
        if (disposed) return;
        const original = pdfPage.getViewport({ scale: 1 });
        const viewport = pdfPage.getViewport({ scale: Math.min(2400 / original.width, 4000 / original.height) });
        // Each task owns its canvas. A cancelled task can never overwrite a newer page.
        const buffer = window.document.createElement('canvas');
        buffer.width = Math.ceil(viewport.width);
        buffer.height = Math.ceil(viewport.height);
        const context = buffer.getContext('2d');
        if (!context) throw new Error('Canvas unavailable');
        task = pdfPage.render({ canvas: buffer, canvasContext: context, viewport });
        await task.promise;
        if (disposed || !canvasRef.current) return;
        const canvas = canvasRef.current;
        canvas.width = buffer.width;
        canvas.height = buffer.height;
        const visibleContext = canvas.getContext('2d');
        if (!visibleContext) throw new Error('Canvas unavailable');
        visibleContext.drawImage(buffer, 0, 0);
        handleMetadata({ ratio: original.width / original.height, totalPages: loaded.document.numPages });
        setLoading(false);
      } catch {
        if (!disposed) {
          setError('PDF 페이지를 표시하지 못했어요. 페이지 번호를 확인해주세요.');
          setLoading(false);
        }
      }
    };
    void render();
    return () => {
      disposed = true;
      task?.cancel();
    };
  }, [loaded, file, page, handleMetadata]);

  return (
    <>
      <canvas ref={canvasRef} style={{ visibility: loading || error ? 'hidden' : 'visible' }} />
      {(loading || error) && (
        <StatusContainer role={error ? 'alert' : 'status'}>
          <TextBlock
            text={error || 'PDF 불러오는 중…'}
            design={{ text: { size: 'small', color: error ? color.RED_3 : color.GRAY_1 } }}
          />
        </StatusContainer>
      )}
    </>
  );
};

export default PdfBackground;
